import axios from 'axios'
import { API_CONFIG, isPublicApi, needsBrandHeader, BRAND_HEADER_NAME } from '@/config/api'
import { getAuthHeader, logout } from '@/utils/auth'
import { brandCode } from '@/branding'

// 根据环境创建不同的实例
const instance = axios.create({
  baseURL: process.env.NODE_ENV === 'development' ? '' : API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  },
  maxRedirects: 5, // 允许最多5次重定向
  // 在重定向时保留 headers
  // 在重定向时保留 headers
  beforeRedirect: (options, { headers }) => {
    // 获取 Authorization header (兼容大小写)
    const token = headers.Authorization || headers.authorization;
    
    if (token) {
      options.headers['Authorization'] = token;
    } else {
      // 如果原请求头中没有，尝试重新获取
      const authHeader = getAuthHeader();
      if (authHeader) {
        options.headers['Authorization'] = authHeader;
      }
    }
  }
});

// 请求拦截器
instance.interceptors.request.use(
  config => {
    // 判断是否为公开请求
    const publicRequest = isPublicApi(config.url, config.method);

    if (!publicRequest) {
      // 添加认证token
      const authHeader = getAuthHeader();
      console.log('🔐 [请求拦截器] 获取到的 authHeader:', authHeader);
      
      if (authHeader) {
        config.headers['Authorization'] = authHeader;
        console.log('✅ [请求拦截器] 已添加 Authorization header');
      } else {
        console.warn('⚠️ [请求拦截器] 未找到 token，请求可能失败');
      }
    } else {
      // 公开 API：确保不携带 Authorization 授权头
      delete config.headers['Authorization'];
      delete config.headers['authorization'];
      console.log('ℹ️ [请求拦截器] 公开请求，跳过添加 Authorization header');
    }

    // 部分 IAM 接口要求携带品牌标识请求头（Ext-Brand：MT / LPT）
    if (needsBrandHeader(config.url, config.method)) {
      config.headers[BRAND_HEADER_NAME] = brandCode();
      console.log(`🏷️ [请求拦截器] 已添加 ${BRAND_HEADER_NAME} header:`, config.headers[BRAND_HEADER_NAME]);
    }

    console.log('📋 [请求拦截器] 最终请求头:', config.headers);
    // 添加MCP-Token（如果需要）
    if (config.url && config.url.includes('/mcp/')) {
      config.headers['MCP-Token'] = 'APS-BWIZ2Qhv4YAVRCwAfL8XKIrRfSprpac3';
    }
    
    // 打印完整的实际通讯地址
    // 开发环境下 baseURL 为空，使用代理，所以显示前端请求路径
    // 生产环境下 baseURL 有值，显示完整URL
    const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
    const isDev = process.env.NODE_ENV === 'development';
    
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📤 [浏览器] 发送请求:');
    console.log('   方法:', config.method?.toUpperCase());
    console.log('   请求路径:', config.url);
    if (!isDev) {
      console.log('   完整地址:', fullUrl);
    } else {
      console.log('   说明: 开发环境使用代理，实际地址请查看终端输出');
    }
    if (config.params) {
      console.log('   URL参数:', config.params);
    }
    if (config.data) {
      console.log('   请求数据:', config.data);
      console.log(' config.data', config.data);
      console.log(' config.data.headers', config.data.headers);
      // console.log(' config.data.headers.Authorization', config.data.headers.Authorization);
      // console.log(' config.data.headers.Authorization', config.data.headers.Authorization);
    }
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    
    return config;
  },
  error => {
    console.error('❌ 请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
instance.interceptors.response.use(
  response => {
    // 打印响应信息
    const isDev = process.env.NODE_ENV === 'development';
    const fullUrl = response.config.baseURL ? `${response.config.baseURL}${response.config.url}` : response.config.url;
    
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📥 [浏览器] 收到响应:');
    console.log('   请求路径:', response.config.url);
    if (!isDev) {
      console.log('   完整地址:', fullUrl);
    }
    console.log('   状态码:', response.status);
    console.log('   响应数据:', response.data);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    
    return response.data || response;
  },
  error => {
    // 打印错误信息
    if (error.config) {
      const isDev = process.env.NODE_ENV === 'development';
      const fullUrl = error.config.baseURL ? `${error.config.baseURL}${error.config.url}` : error.config.url;
      
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('❌ [浏览器] 响应错误:');
      console.log('   请求路径:', error.config.url);
      if (!isDev) {
        console.log('   完整地址:', fullUrl);
      }
      console.log('   错误信息:', error.message);
      if (error.response) {
        console.log('   状态码:', error.response.status);
        console.log('   错误数据:', error.response.data);
      }
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    } else {
      console.error('❌ [浏览器] 响应错误:', error);
    }
    
    // 处理401未授权错误（公开请求不触发登出跳转）
    const publicRequest = error.config && isPublicApi(error.config.url, error.config.method);

    if (error.response && error.response.status === 401 && !publicRequest) {
      // 清除所有认证信息
      logout();
      
      // 重定向到登录页面（如果需要）
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

export default instance;
